import {
    Component,
    computed,
    effect,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    signal,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {PositiveNumberOnlyDirective} from '../../../../Shared/Directives/Positive-number-only.directive';
import {SearchBuyerComponent, SearchCustomerComponent, SearchDepartmentComponent} from '../../../../../../shared';
import {Router} from '@angular/router';
import {APIResponse, AuthService, DotLoadingServices} from '../../../../../../../core';
import {NotificationService} from '../../../../../../services/notification.service';
import {ProductSampleCreateDTO} from '../../../../../../interfaces/product-sample.types';
import {DepartmentService} from '../../../../../../services/department.service';
import {
    FinishingTypeGroup,
    ProductSampleFinishingPayload,
    SelectedFinishing
} from '../../../../../../interfaces/department.entity';
import {ProductSampleNewService} from '../../../../../../shared/common/NEW/services/product-sample-new.service';
import {DatePipe, NgClass} from '@angular/common';
import {LabelTypesService} from '../../../services/label-types.service';
import {LabelTypeGetAllDTO} from '../../../interface/label-type.interface';
import {InventoryItemsService} from '../../../services/inventory-items.service';
import {InventoryItemFindOldDTO} from '../../../interface/Inventory-Item.interface';
import {
    SearchSalesExecutive
} from '../../../../../../shared/common/search-sales-owner/search-sales-executive.component';
import {tap} from 'rxjs/operators';
import {HsCodeDTO} from '../../../../../../interfaces/hs-code.entity';
import {HsCodeService} from '../../../../../../services';
import {firstValueFrom, Observable} from 'rxjs';
import {ProductSampleGetDTO, ProductSampleCompliance} from '../../../../../../shared/common/NEW/interfaces/product-sample.entity';
import {ProductDevelopmentHandleService} from '../../../services/product-development-handle.service';
import {SelectOnClickDirective} from '../../../../Shared/Directives/select-on-click.directive';
import {ComplianceCertificateService} from '../../../../compliance-certificate/services/compliance-certificate.service';
import {
    ComplianceCertificateDTO,
    FSC_COMPLIANCE_TYPE_ID
} from '../../../../compliance-certificate/interface/compliance-certificate.interface';

@Component({
    selector: 'app-product-sample-create',
    imports: [
        FormsModule,
        PositiveNumberOnlyDirective,
        DatePipe,
        SearchCustomerComponent,
        SearchDepartmentComponent,
        SearchBuyerComponent,
        SearchSalesExecutive,
        SelectOnClickDirective,
        NgClass,
    ],
    templateUrl: './product-sample-create.component.html',
    styleUrl: './product-sample-create.component.scss'
})
export class ProductSampleCreateComponent implements OnInit, OnChanges, OnDestroy {
    public sampleHandleService = inject(ProductDevelopmentHandleService);
    public productSampleService = inject(ProductSampleNewService);
    public hsCodeService = inject(HsCodeService);
    public inventoryItemsService = inject(InventoryItemsService);
    public notification = inject(NotificationService);
    public labelTypesService = inject(LabelTypesService);
    public loading = inject(DotLoadingServices);
    public router = inject(Router);
    public authService = inject(AuthService);
    public departmentService = inject(DepartmentService);
    public complianceCertificateService = inject(ComplianceCertificateService);

    @Input() sampleId: number | null = null;
    sampleIdSignal = signal<number | null>(null);
    sampleCreated = signal<boolean>(false);
    originalSample = signal<ProductSampleCreateDTO | null>(null); // Store original sample data

    public today: Date = new Date();
    public salesExecutiveStatus = signal<boolean>(false);
    public inventoryItemFindDTOS = signal<InventoryItemFindOldDTO[]>([]);
    public selectedLabel = signal<number>(-1);
    public selectedProduct = signal<number>(-1);
    public attachmentType = signal<string>('');
    public createdSample = signal<string>('');
    public createdSampleID = signal<number>(0);
    public departmentFinishings = signal<FinishingTypeGroup[]>([]);
    public selectedFinishings = signal<SelectedFinishing[]>([]);
    public finishingsLoading = signal<boolean>(false);
    public isFsc = signal<boolean>(false);
    public selectedCertificateIds = signal<number[]>([]);
    public fscCertificates = signal<ComplianceCertificateDTO[]>([]);
    public certificatesLoading = signal<boolean>(false);

    public searchParams = signal({
        item_code: '',
        item_name: '',
        item_type: -1,
        group: -1,
        label_type: -1,
        page_number: 1,
        items_per_page: 10
    });

    public productSample = signal<ProductSampleCreateDTO>({
        sampleName: '',
        sampleQuantity: 0,
        manualCustomer: '',
        contactPerson: '',
        buyer: -1,
        customer: -1,
        sampleOwner: -1,
        requiredDate: '',
        addedBy: '',
        product: -1,
        department: -1,
        note: '',
        psHsCode: -1,
        isOriginalSample: false,
        isImage: false,
        finishings: [],
        complianceCertificates: []
    });

    @ViewChild(SearchBuyerComponent) searchBuyerComponent!: SearchBuyerComponent;

    getLabelTypes = computed(() =>
        this.labelTypesService.all()?.filter((label: LabelTypeGetAllDTO) => label) ?? []
    );

    getHsCodes = computed(() =>
        this.hsCodeService.all()?.filter((hsCodes: HsCodeDTO) => hsCodes) ?? []
    );

    constructor() {
        effect(() => {
            const id = this.sampleIdSignal();
            if (id !== null) {
                this.onSampleSelected({ id });
            }
        });
    }

    ngOnInit() {
        this.labelTypesService.getAll().subscribe({
            next: () => { },
            error: (error) => {
                this.notification.set({
                    type: 'error',
                    message: 'Failed to load label types.',
                    timeout: 3000
                });
            }
        });

        if (!this.sampleHandleService.selectedSampleId()) {
            this.sampleHandleService._isEditingModeEnabled.set(true);
        } else {
            this.sampleHandleService._isEditingModeEnabled.set(false);
        }

        this.loadFscCertificates();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['sampleId']) {
            this.sampleIdSignal.set(this.sampleId);
        }
    }

    public onLabelSelected(value: number): Promise<void> {
        this.selectedLabel.set(value);
        this.searchParams.update(params => ({
            ...params,
            label_type: value
        }));
        this.productSample.update(current => ({ ...current, product: -1 }));

        const labelId = Number(value);
        const selectedLabelType = this.getLabelTypes().find(label => label.id === labelId);

        const departmentId = selectedLabelType && selectedLabelType.departmentId > 0 ? selectedLabelType.departmentId : -1;
        const hsCodeId = selectedLabelType && selectedLabelType.hsCode?.hsCodeId > 0 ? selectedLabelType.hsCode.hsCodeId : -1;

        if (!this.productSample().productSampleId) {
            this.productSample.update(current => ({
                ...current,
                department: departmentId,
                psHsCode: hsCodeId
            }));
            this.loadDepartmentFinishings(departmentId);
        }

        return new Promise((resolve, reject) => {
            this.fetchInventoryItems().subscribe({
                next: () => resolve(),
                error: (err: any) => reject(err)
            });
        });
    }

    public onProductSelected(value: any): void {
        const productId = Number(value);
        if (isNaN(productId)) {
            this.selectedProduct.set(-1);
            this.productSample.update(current => ({ ...current, product: -1 }));
            return;
        }
        const isValidProduct = this.inventoryItemFindDTOS().some(item => item.inventoryId === productId);
        if (isValidProduct) {
            this.selectedProduct.set(productId);
            this.productSample.update(current => ({ ...current, product: productId }));
        } else {
            this.selectedProduct.set(-1);
            this.productSample.update(current => ({ ...current, product: -1 }));
        }
    }

    public onFieldSelected(field: keyof ProductSampleCreateDTO, value: any) {
        let finalValue = value;

        if (field === 'sampleOwner' && typeof value === 'object' && value?.employeeId) {
            finalValue = value.employeeId;
        } else if (field === 'contactPerson' && typeof value === 'object') {
            finalValue = [
                value?.firstName?.trim(),
                value?.midName?.trim(),
                value?.lastName?.trim()
            ]
                .filter(part => part)
                .join(' ');
        } else if (field === 'sampleOwner' || field === 'buyer' || field === 'customer' || field === 'department') {
            finalValue = value ?? -1;
        }

        this.productSample.update(current => ({ ...current, [field]: finalValue }));

        if (field === 'department') {
            this.loadDepartmentFinishings(Number(finalValue));
        }

        if (field === 'customer' && finalValue === -1) {
            this.productSample.update(current => ({
                ...current,
                manualCustomer: '',
                contactPerson: '',
                buyer: -1
            }));
            if (this.searchBuyerComponent) {
                this.searchBuyerComponent.clearSelection();
            }
        }
    }

    protected fetchInventoryItems(): Observable<any> {
        return this.inventoryItemsService.findOld(this.searchParams()).pipe(
            tap({
                next: (response) => {
                    this.inventoryItemFindDTOS.set(response.data.data || []);
                },
                error: (error) => {
                    console.error('Error fetching inventory items:', error);
                    this.notification.set({
                        type: 'error',
                        message: 'Failed to fetch inventory items.'
                    });
                }
            })
        );
    }

    public updateField(field: keyof ProductSampleCreateDTO, value: any) {
        this.productSample.update(current => ({ ...current, [field]: value }));
    }

    public onAttachmentTypeChange(value: string) {
        this.attachmentType.set(value);
        this.productSample.update(current => ({
            ...current,
            isImage: value === 'image'
        }));
    }

    public loadDepartmentFinishings(departmentId: number, preserveSelection = false): void {
        if (!departmentId || departmentId <= 0) {
            this.departmentFinishings.set([]);
            if (!preserveSelection) {
                this.selectedFinishings.set([]);
                this.syncFinishingsToProductSample();
            }
            return;
        }

        if (!preserveSelection) {
            this.selectedFinishings.set([]);
            this.syncFinishingsToProductSample();
        }

        this.finishingsLoading.set(true);
        this.departmentService.getFinishings(departmentId).subscribe({
            next: (res) => {
                this.departmentFinishings.set(res.data || []);
                this.finishingsLoading.set(false);
            },
            error: () => {
                this.departmentFinishings.set([]);
                this.finishingsLoading.set(false);
                this.notification.set({
                    type: 'error',
                    message: 'Failed to load department finishings.',
                    timeout: 3000
                });
            }
        });
    }

    public isFinishingSelected(finishingId: number): boolean {
        return this.selectedFinishings().some(f => f.finishingId === finishingId);
    }

    public onFinishingToggle(
        finishingId: number,
        name: string,
        type: string,
        event: Event
    ): void {
        const isChecked = (event.target as HTMLInputElement).checked;

        if (isChecked) {
            const nextSequence = this.selectedFinishings().length + 1;
            this.selectedFinishings.update(current => [
                ...current,
                { finishingId, name, type, sequence: nextSequence, isRequired: true, remarks: '' }
            ]);
        } else {
            this.selectedFinishings.update(current =>
                current
                    .filter(f => f.finishingId !== finishingId)
                    .map((f, index) => ({ ...f, sequence: index + 1 }))
            );
        }
        this.syncFinishingsToProductSample();
    }

    public onFinishingRequiredChange(finishingId: number, isRequired: boolean): void {
        this.selectedFinishings.update(current =>
            current.map(f => f.finishingId === finishingId ? { ...f, isRequired } : f)
        );
        this.syncFinishingsToProductSample();
    }

    public onFinishingRemarksChange(finishingId: number, remarks: string): void {
        this.selectedFinishings.update(current =>
            current.map(f => f.finishingId === finishingId ? { ...f, remarks } : f)
        );
        this.syncFinishingsToProductSample();
    }

    public moveFinishing(finishingId: number, direction: 'up' | 'down'): void {
        const current = [...this.selectedFinishings()];
        const index = current.findIndex(f => f.finishingId === finishingId);
        if (index === -1) return;

        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= current.length) return;

        [current[index], current[targetIndex]] = [current[targetIndex], current[index]];
        this.selectedFinishings.set(
            current.map((f, i) => ({ ...f, sequence: i + 1 }))
        );
        this.syncFinishingsToProductSample();
    }

    private syncFinishingsToProductSample(): void {
        const finishings: ProductSampleFinishingPayload[] = this.selectedFinishings().map(f => {
            const payload: ProductSampleFinishingPayload = {
                finishingId: f.finishingId,
                sequence: f.sequence,
                isRequired: f.isRequired ? 1 : 0
            };
            if (f.remarks?.trim()) {
                payload.remarks = f.remarks.trim();
            }
            return payload;
        });
        this.productSample.update(current => ({ ...current, finishings }));
    }

    private restoreSelectedFinishings(
        finishings: ProductSampleFinishingPayload[],
        availableGroups: FinishingTypeGroup[]
    ): void {
        const finishingLookup = new Map<number, { name: string; type: string }>();
        availableGroups.forEach(group => {
            group.finishings.forEach(f => {
                finishingLookup.set(f.id, { name: f.name, type: group.type });
            });
        });

        const restored: SelectedFinishing[] = [...finishings]
            .sort((a, b) => a.sequence - b.sequence)
            .map(f => {
                const info = finishingLookup.get(f.finishingId);
                return {
                    finishingId: f.finishingId,
                    name: info?.name ?? `Finishing #${f.finishingId}`,
                    type: info?.type ?? '',
                    sequence: f.sequence,
                    isRequired: f.isRequired === 1,
                    remarks: f.remarks ?? ''
                };
            });

        this.selectedFinishings.set(restored);
        this.syncFinishingsToProductSample();
    }

    public formatFinishingType(type: string): string {
        return type.charAt(0) + type.slice(1).toLowerCase();
    }

    public loadFscCertificates(): void {
        this.certificatesLoading.set(true);
        this.complianceCertificateService.getByType(FSC_COMPLIANCE_TYPE_ID).subscribe({
            next: (type) => {
                this.fscCertificates.set(type.certificates ?? []);
                this.certificatesLoading.set(false);
            },
            error: () => {
                this.fscCertificates.set([]);
                this.certificatesLoading.set(false);
                this.notification.set({
                    type: 'error',
                    message: 'Failed to load FSC certificates.',
                    timeout: 3000
                });
            }
        });
    }

    public onFscModeChange(isFsc: boolean): void {
        this.isFsc.set(isFsc);
        if (!isFsc) {
            this.selectedCertificateIds.set([]);
        }
        this.syncComplianceToProductSample();
    }

    public isCertificateSelected(certificateId: number): boolean {
        return this.selectedCertificateIds().includes(certificateId);
    }

    public onCertificateToggle(certificateId: number, event: Event): void {
        const isChecked = (event.target as HTMLInputElement).checked;
        this.selectedCertificateIds.update((current) => {
            if (isChecked) {
                return current.includes(certificateId) ? current : [...current, certificateId];
            }
            return current.filter((id) => id !== certificateId);
        });
        this.syncComplianceToProductSample();
    }

    public formatCertificateLabel(certificate: ComplianceCertificateDTO): string {
        return certificate.certificateCode?.trim()
            || certificate.certificateName?.trim()
            || `Certificate #${certificate.id}`;
    }

    private syncComplianceToProductSample(): void {
        this.productSample.update((current) => ({
            ...current,
            complianceCertificates: this.isFsc()
                ? this.selectedCertificateIds()
                    .filter((id) => id > 0)
                    .map((complianceCertificateId) => ({complianceCertificateId}))
                : []
        }));
    }

    private applyLoadedCompliance(certificates: ProductSampleCompliance[]): void {
        const certificateIds = certificates
            .map((certificate) => certificate.complianceCertificateId)
            .filter((id) => id > 0);

        this.isFsc.set(certificateIds.length > 0);
        this.selectedCertificateIds.set(certificateIds);
        this.syncComplianceToProductSample();
    }

    private parseComplianceCertificates(value: unknown): ProductSampleCompliance[] {
        if (!value) {
            return [];
        }

        let parsed: unknown = value;
        if (typeof value === 'string') {
            try {
                parsed = JSON.parse(value);
            } catch {
                return [];
            }
        }

        return Array.isArray(parsed) ? parsed : [];
    }

    public isValidCreation(): boolean {
        const s = this.productSample();
        return (
            !s.sampleName.trim() ||
            s.sampleQuantity <= 0 ||
            !s.requiredDate ||
            s.buyer === -1 ||
            s.sampleOwner === -1 ||
            this.selectedLabel() === -1 ||
            s.product <= 0 ||
            s.psHsCode === -1 ||
            (this.isFsc() && this.selectedCertificateIds().length === 0)
        );
    }

    public async createProductSample(): Promise<void> {
        if (this.isValidCreation()) {
            this.notification.set({
                type: 'alert',
                message: 'Please fill all required fields to continue.',
            });
            return;
        }

        try {
            this.loading.set(true);
            this.syncComplianceToProductSample();
            const productSampleData = this.productSample();
            const isUpdate = !!productSampleData.productSampleId && productSampleData.productSampleId !== -1;
            const basePayload = {
                sampleName: String(productSampleData.sampleName || ''),
                sampleQuantity: Number(productSampleData.sampleQuantity),
                manualCustomer: String(productSampleData.manualCustomer || ''),
                contactPerson: String(productSampleData.contactPerson || ''),
                isActive: 'A',
                buyer: Number(productSampleData.buyer),
                customer: Number(productSampleData.customer),
                sampleOwner: Number(productSampleData.sampleOwner),
                product: Number(productSampleData.product),
                department: Number(productSampleData.department),
                requiredDate: String(productSampleData.requiredDate || ''),
                note: String(productSampleData.note || ''),
                psHsCode: parseInt(String(productSampleData.psHsCode), 10),
                isOriginalSample: productSampleData.isOriginalSample ? 1 : 0,
                isImage: productSampleData.isImage ? 1 : 0,
                finishings: productSampleData.finishings ?? [],
                complianceCertificates: productSampleData.complianceCertificates ?? []
            };
            const payload = isUpdate
                ? { ...basePayload, updatedBy: String(this.authService.username() || '') }
                : { ...basePayload, addedBy: String(this.authService.username() || '') };

            const operation$ = isUpdate
                ? this.productSampleService.update(productSampleData.productSampleId!, payload)
                : this.productSampleService.create(payload);

            const res = await firstValueFrom(operation$);

            this.notification.set({
                type: 'success',
                message: `Product sample ${isUpdate ? 'updated' : 'created'} successfully.`,
            });
            this.productSampleService.notifySampleCreated();
            this.sampleHandleService._isEditingModeEnabled.set(false);

            if (!isUpdate) {
                this.onSampleSelected({ id: res.data.sampleId });
                this.sampleHandleService.editingSampleCode.set(res.data.productSampleCode);
                this.sampleHandleService.selectedSampleId.set(res.data.sampleId);
                this.createdSampleID.set(res.data.sampleId);
                this.sampleHandleService.checkSampleValidations(res.data.sampleId!);
                this.sampleHandleService._isEditingModeEnabled.set(false);
            } else {
                this.originalSample.set({ ...this.productSample() });
            }

        } catch (error) {
            const isUpdate = !!this.productSample().productSampleId && this.productSample().productSampleId !== -1;
            this.notification.set({
                type: 'error',
                message: `Failed to ${isUpdate ? 'update' : 'create'} product sample.`,
            });
            console.error(error);
        } finally {
            this.loading.set(false);
        }
    }

    handleSalesOwnerStatus(status: boolean) {
        this.salesExecutiveStatus.set(status);
    }

    onSampleSelected(selectedProductSample: any): void {
        if (selectedProductSample.id) {
            this.loading.set(true);
            this.productSampleService.getById(selectedProductSample.id).subscribe({
                next: (res: APIResponse<ProductSampleGetDTO>) => {
                    if (!res.data) {
                        this.notification.set({
                            type: 'error',
                            message: 'No product sample data found.',
                        });
                        this.loading.set(false);
                        return;
                    }
                    const mappedSample: ProductSampleCreateDTO = this.mapToProductSampleCreateDTO(res.data);
                    this.productSample.set(mappedSample);
                    this.originalSample.set({ ...mappedSample }); // Store original sample data
                    this.attachmentType.set(mappedSample.isImage ? 'image' : 'pdf');
                    this.applyLoadedCompliance(this.parseComplianceCertificates(res.data.complianceCertificates));

                    const productTypeId = Number(res.data.productType?.productTypeId);
                    if (isNaN(productTypeId)) {
                        console.warn(`Invalid productTypeId: ${res.data.productType?.productTypeId}`);
                        this.notification.set({
                            type: 'error',
                            message: 'Invalid product type ID.',
                        });
                        this.loading.set(false);
                        return;
                    }
                    this.selectedProduct.set(productTypeId);

                    const labelTypeId = Number(res.data.productType?.labelTypeID);
                    if (isNaN(labelTypeId)) {
                        this.notification.set({
                            type: 'error',
                            message: 'Invalid label type ID.',
                        });
                        this.loading.set(false);
                        return;
                    }

                    const savedFinishings = mappedSample.finishings ?? [];
                    const departmentId = mappedSample.department;

                    this.onLabelSelected(labelTypeId).then(() => {
                        this.onProductSelected(productTypeId);
                        if (departmentId > 0) {
                            this.departmentService.getFinishings(departmentId).subscribe({
                                next: (finishingRes) => {
                                    this.departmentFinishings.set(finishingRes.data || []);
                                    if (savedFinishings.length > 0) {
                                        this.restoreSelectedFinishings(savedFinishings, finishingRes.data || []);
                                    }
                                },
                                error: () => this.departmentFinishings.set([])
                            });
                        }
                        this.loading.set(false);
                    }).catch((err) => {
                        console.error('Error in onLabelSelected:', err);
                        this.notification.set({
                            type: 'error',
                            message: 'Failed to process label selection.',
                        });
                        this.loading.set(false);
                    });
                },
                error: (err) => {
                    console.error('Error fetching product sample:', err);
                    this.notification.set({
                        type: 'error',
                        message: 'Failed to fetch product sample.',
                    });
                    this.loading.set(false);
                }
            });
        }
    }

    private mapToProductSampleCreateDTO(dto: ProductSampleGetDTO): ProductSampleCreateDTO {
        return {
            productSampleId: dto.productSampleId ?? -1,
            sampleName: dto.sampleName || '',
            sampleQuantity: dto.sampleQuantity || 0,
            manualCustomer: dto.manualCustomer || '',
            contactPerson: dto.contactPerson || '',
            buyer: dto.buyer?.buyerId ?? -1,
            customer: dto.customer?.customerId ?? -1,
            sampleOwner: dto.sampleOwner?.employeeId ?? -1,
            requiredDate: dto.reqDate || '',
            addedBy: '',
            product: dto.productType.productTypeId ?? -1,
            department: dto.department?.departmentId ?? -1,
            note: dto.note || '',
            psHsCode: dto.hsCode?.hsCodeId ?? -1,
            isOriginalSample: dto.isOriginal,
            isImage: dto.isImg,
            finishings: (dto.finishings ?? []).map(f => ({
                finishingId: f.finishingId,
                sequence: f.sequence,
                isRequired: f.isRequired,
                remarks: f.remarks
            })),
            complianceCertificates: this.parseComplianceCertificates(dto.complianceCertificates).map((certificate) => ({
                complianceCertificateId: certificate.complianceCertificateId
            }))
        };
    }

    ngOnDestroy(): void {
        this.cancel();
    }

    public cancel() {
        this.productSample.set({
            sampleName: '',
            productSampleId: undefined,
            sampleQuantity: 0,
            manualCustomer: '',
            contactPerson: '',
            buyer: -1,
            customer: -1,
            sampleOwner: -1,
            requiredDate: '',
            addedBy: '',
            product: -1,
            department: -1,
            note: '',
            psHsCode: -1,
            isOriginalSample: false,
            isImage: false,
            finishings: [],
            complianceCertificates: []
        });
        this.departmentFinishings.set([]);
        this.selectedFinishings.set([]);
        this.selectedLabel.set(-1);
        this.selectedProduct.set(-1);
        this.isFsc.set(false);
        this.selectedCertificateIds.set([]);
        this.attachmentType.set('');
        this.searchParams.set({
            item_code: '',
            item_name: '',
            item_type: -1,
            group: -1,
            label_type: -1,
            page_number: 1,
            items_per_page: 10
        });
        this.inventoryItemFindDTOS.set([]);
        this.sampleIdSignal.set(null);
        this.originalSample.set(null);

        if (this.searchBuyerComponent) {
            this.searchBuyerComponent.clearSelection();
        }
    }

    public closeManager() {
        this.sampleCreated.set(false);
        this.cancel();
        this.router.navigate(['product-development']).then(r => this.sampleHandleService.resetSignals());
    }

    public cancelEditMode() {
        if (this.productSample().productSampleId && this.originalSample()) {
            this.productSample.set({ ...this.originalSample()! });
            this.selectedLabel.set(this.originalSample()!.product !== -1 ? this.getLabelTypes().find(label => label.id === this.originalSample()!.product) ? this.originalSample()!.product : -1 : -1);
            this.selectedProduct.set(this.originalSample()!.product);
            this.attachmentType.set(this.originalSample()!.isImage ? 'image' : 'pdf');
            this.isFsc.set((this.originalSample()!.complianceCertificates?.length ?? 0) > 0);
            this.selectedCertificateIds.set(
                (this.originalSample()!.complianceCertificates ?? [])
                    .map((certificate) => certificate.complianceCertificateId)
                    .filter((id) => id > 0)
            );
            this.searchParams.update(params => ({
                ...params,
                label_type: this.selectedLabel()
            }));
        } else {
            this.cancel();
        }
        this.onSampleSelected(this.sampleHandleService.selectedSampleId());
        this.sampleHandleService._isEditingModeEnabled.set(false);
    }

}